import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Home, ChevronLeft } from 'lucide-react'
import { IS_GOLF_ELITE } from '../config/siteMode.js'
import { onPlatformAuth } from '../lib/platformAuth.js'

// Global Back + Home control so no screen is ever a dead end. Rendered ONCE at
// the app root (App.jsx) → present on every interactive route, from one shared
// component (no per-screen re-skins). Brand-aware: the pill uses the --ge-primary
// token, so it is navy on Golf Elite and forest-green on PBCC automatically.
//
// Hidden on passive DISPLAY boards (TV screens, slideshow, the leaderboard wall,
// the GPS map) — those are driven by /tvcontrol or a remote, never hand-navigated,
// and a floating button would sit on top of the broadcast.
function isHidden(pathname) {
  if (pathname === '/' || pathname === '/live') return true
  if (pathname === '/signup') return true // the signup page renders its own header Back/Home
  if (pathname === '/tv' || pathname.startsWith('/tv/')) return true // TV boards, NOT /tvcontrol
  if (pathname === '/leaderboard') return true
  if (pathname === '/slideshow' || pathname === '/photowall' || pathname === '/photo') return true
  if (pathname === '/map' || pathname.startsWith('/gps')) return true
  return false
}

export default function BrandNav() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  // Golf Elite only: track platform auth so we can suppress the floating pills on
  // the logged-out admin/billing gate — that screen renders its own glass header
  // with Back/Home, and the navy pills read poorly on the dark hero. (No-op for
  // PBCC/other builds, so they stay byte-identical.)
  const [geUser, setGeUser] = useState(undefined)
  useEffect(() => { if (!IS_GOLF_ELITE) return; return onPlatformAuth(setGeUser) }, [])
  if (isHidden(pathname)) return null
  if (IS_GOLF_ELITE && !geUser && (pathname.startsWith('/admin') || pathname.startsWith('/billing'))) return null
  // Golf Elite: the admin home/dashboard IS home — nowhere to go back to, so no
  // floating Back/Home pills here (other screens keep them).
  if (IS_GOLF_ELITE && (pathname === '/admin' || pathname === '/admin/')) return null

  const btn = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '7px 12px',
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1,
    color: '#FFFFFF',
    background: 'var(--ge-primary)',
    border: '1px solid rgba(255,255,255,0.35)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.22)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 'calc(env(safe-area-inset-top, 0px) + 8px)',
        left: 'calc(env(safe-area-inset-left, 0px) + 8px)',
        zIndex: 9999,
        display: 'flex',
        gap: 8,
      }}
    >
      <button type="button" style={btn} onClick={() => navigate(-1)} aria-label="Back">
        <ChevronLeft size={16} strokeWidth={2.4} /> Back
      </button>
      <button type="button" style={btn} onClick={() => navigate('/')} aria-label="Home">
        <Home size={16} strokeWidth={2.4} /> Home
      </button>
    </div>
  )
}
